import {
	useMemo,
	useState,
	useImperativeHandle,
	forwardRef,
	useEffect,
	useCallback
} from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { extractCheckResultError } from '@/lib/checkResult';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showDeleteColumn } from '@/lib/featureFlags';

import {
	CUSTOMERS_LIST_QUERY,
	CUSTOMER_DELETE_MUTATION,
	CUSTOMER_QUERY
} from './customers.gql.tsx';
import { CustomerDetails, CustomerDetailsHeader } from './customer-details';
import {
	selectCustomerAfterSave,
	type CustomerListRecord
} from './customer-list-selection';

interface CustomersListResponse {
	customers?: {
		nodes?: CustomerListRecord[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

// Types for full customer query used in cloning
interface CustomerQueryContact {
	customerContactId: string;
	contactTypeId?: number | null;
	contactValue?: string | null;
	isPrimary: boolean;
	notes?: string | null;
	isDeleted?: boolean;
}

interface CustomerQueryAddress {
	customerAddressId: string;
	addressId: string;
	addressTypeId?: number | null;
	address?: {
		streetAddress?: string | null;
		city?: string | null;
		state?: string | null;
		postalCode?: string | null;
		country?: string | null;
	} | null;
}

interface CustomerQueryBankDetail {
	bankDetailsId: string;
	bankName?: string | null;
	accountNumber?: string | null;
	iban?: string | null;
	bankCode?: string | null;
	accountHolderName?: string | null;
	isPrimary: boolean;
	isDeleted: boolean;
}

interface CustomerQueryBeneficiary {
	customerBeneficiaryId: string;
	beneficiaryId: string;
	isActive: boolean;
	notes?: string | null;
	relationshipType?: string | null;
	isDeleted: boolean;
	beneficiary?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany: boolean;
	} | null;
}

interface CustomerQueryVehicle {
	vehicleId: string;
	plateNumber?: string | null;
	vehicleTypeId?: number | null;
	make?: string | null;
	model?: string | null;
	color?: string | null;
	isActive: boolean;
}

interface CustomerFullRecord {
	customerId: string;
	isCompany: boolean;
	firmName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	fiscalNumber?: string | null;
	marketingOptIn: boolean;
	notes?: string | null;
	isActive: boolean;
	customerCategoryId: string;
	preferredLanguage?: string | null;
	companyRegistrationNumber?: string | null;
	industry?: string | null;
	companySize?: string | null;
	dateOfBirth?: string | null;
	gender?: string | null;
	idNumber?: string | null;
	idType?: string | null;
	nationality?: string | null;
	customerContacts?: CustomerQueryContact[] | null;
	customerAddresses?: CustomerQueryAddress[] | null;
	bankDetails?: CustomerQueryBankDetail[] | null;
	customerBeneficiaries?: CustomerQueryBeneficiary[] | null;
	vehicles?: CustomerQueryVehicle[] | null;
}

interface CustomerQueryResponse {
	customer?: CustomerFullRecord | null;
}

export interface CustomersListRef {
	selectCustomerAfterSave: (customerId: string) => Promise<void>;
}

const formatDisplayName = (
	record?: CustomerListRecord,
	translate?: (key: string) => string
) => {
	if (!record) return '';

	const businessFallback = translate
		? translate('details.businessCustomer')
		: 'Business customer';
	const unnamedFallback = translate
		? translate('details.unnamedCustomer')
		: 'Unnamed customer';

	if (record.displayName?.trim()) {
		return record.displayName.trim();
	}

	if (record.isCompany) {
		return record.firmName?.trim() || businessFallback;
	}

	const parts = [record.firstName, record.lastName]
		.map(part => part?.trim())
		.filter(Boolean);

	if (parts.length > 0) {
		return parts.join(' ');
	}

	return record.email?.trim() || unnamedFallback;
};

export const CustomersList = forwardRef<CustomersListRef>((_props, ref) => {
	const { t } = useTranslation('customers');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedCustomer, setSelectedCustomer] =
		useState<CustomerListRecord | null>(null);
	const [localCustomers, setLocalCustomers] = useState<CustomerListRecord[]>(
		[]
	);
	const { goToCreate, goToEdit } = useResourceNavigation('customers');
	const go = useGo();
	const { showDialog } = useConfirmationDialog();

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading, error, refetch, fetchMore } =
		useQuery<CustomersListResponse>(CUSTOMERS_LIST_QUERY, {
			variables: {
				first: itemsToFetch
			}
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.customers?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.customers?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			const result = await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.customers) return prev;

					return {
						...prev,
						customers: {
							...fetchMoreResult.customers,
							nodes: [
								...(prev.customers?.nodes ?? []),
								...(fetchMoreResult.customers.nodes ?? [])
							]
						}
					};
				}
			});
			// Update items to fetch count to preserve loaded items on refetch
			const newItemsCount = result.data?.customers?.nodes?.length ?? 0;
			if (newItemsCount > 0) {
				setItemsToFetch(prev => prev + newItemsCount);
			}
			// Reset local customers to use server data after fetchMore
			setLocalCustomers([]);
		} catch (error) {
			console.error('Error loading more customers:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const handleRefetch = () => {
		setLocalCustomers([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	const [deleteCustomer, { loading: deleteLoading }] = useMutation(
		CUSTOMER_DELETE_MUTATION
	);

	// Sync local customers with server data
	const customers = useMemo(() => {
		const serverCustomers = data?.customers?.nodes ?? [];
		// Always prefer server data - local state is only for optimistic updates
		if (localCustomers.length === 0 || serverCustomers.length === 0) {
			return serverCustomers;
		}
		// Use local state only when both have data (for optimistic updates)
		return localCustomers;
	}, [data?.customers?.nodes, localCustomers]);

	// Expose method to select customer after save via ref
	useImperativeHandle(
		ref,
		() => ({
			selectCustomerAfterSave: async (customerId: string) => {
				await selectCustomerAfterSave({
					customerId,
					client: apolloClient,
					currentCustomers: customers,
					onDataUpdate: setLocalCustomers,
					onCustomerSelect: setSelectedCustomer
				});
			}
		}),
		[apolloClient, customers]
	);

	// Handle customer selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectCustomerId?: string } | null;
		if (state?.selectCustomerId && customers.length > 0 && !loading) {
			const customerIdToSelect = state.selectCustomerId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectCustomerAfterSave({
						customerId: customerIdToSelect,
						client: apolloClient,
						currentCustomers: customers,
						onDataUpdate: setLocalCustomers,
						onCustomerSelect: setSelectedCustomer
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select customer after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		customers.length,
		loading,
		apolloClient,
		customers,
		navigate,
		location.pathname
	]);

	// Helper function to find the next customer to select after deletion
	const findNextCustomerToSelect = (
		deletedCustomer: CustomerListRecord,
		customerList: CustomerListRecord[]
	) => {
		const currentIndex = customerList.findIndex(
			c => c.customerId === deletedCustomer.customerId
		);

		if (customerList.length <= 1) {
			return null;
		}

		// Try to select the next customer (same index after deletion)
		if (currentIndex < customerList.length - 1) {
			return customerList[currentIndex + 1];
		}

		// If deleting the last customer, select the previous one
		if (currentIndex > 0) {
			return customerList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextCustomerToSelect: CustomerListRecord | null,
		updatedCustomers: CustomerListRecord[]
	) => {
		setLocalCustomers([]);
		// First, clear the current selection completely
		setSelectedCustomer(null);

		// Then after a brief delay, set the next customer
		setTimeout(() => {
			if (nextCustomerToSelect && updatedCustomers.length > 0) {
				// Find the customer in the updated list by ID
				const updatedCustomer = updatedCustomers.find(
					c => c.customerId === nextCustomerToSelect?.customerId
				);

				if (updatedCustomer) {
					setSelectedCustomer(updatedCustomer);
				}
			}
		}, 100);
	};

	const handleDeleteCustomer = async (customer: CustomerListRecord) => {
		const confirmed = await showDialog(
			'Delete Customer',
			`Are you sure you want to delete customer "${formatDisplayName(customer, t)}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next customer to select
			const wasSelectedCustomer =
				selectedCustomer?.customerId === customer.customerId;

			// Clear selection immediately if deleting the selected customer
			if (wasSelectedCustomer) {
				setSelectedCustomer(null);
			}

			try {
				const result = await deleteCustomer({
					variables: {
						input: {
							customerId: customer.customerId
						}
					},
					refetchQueries: ['Customers']
				});

				const checkResult = result.data?.deleteCustomer;

				if (checkResult?.success) {
					showMutationSuccessToast('customers', 'delete');

					// Find the next customer to select after deletion (before refetch)
					let nextCustomerToSelect = null;
					if (wasSelectedCustomer) {
						nextCustomerToSelect = findNextCustomerToSelect(
							customer,
							customers
						);
					}

					// Refetch data
					refetch().then(result => {
						const updatedCustomers =
							result.data?.customers?.nodes ?? [];
						handlePostDeletionSelection(
							nextCustomerToSelect,
							updatedCustomers
						);
					});
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete customer: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete customer error:', error);
				toast.error('Failed to delete customer. Please try again.');
			}
		}
	};

	const handleDeleteCustomerById = async (customerId: string) => {
		const customer = customers.find(c => c.customerId === customerId);
		if (customer) {
			await handleDeleteCustomer(customer);
		}
	};

	/**
	 * Builds clone data for the customer create form.
	 * Strips entity IDs so new entities are created, but preserves all field values.
	 */
	const buildCloneData = (customer: CustomerFullRecord) => {
		// Helper to get beneficiary display name
		const getBeneficiaryDisplayName = (beneficiary: CustomerQueryBeneficiary) => {
			if (!beneficiary.beneficiary) return '';
			const ben = beneficiary.beneficiary;
			if (ben.isCompany && ben.firmName) {
				return ben.firmName;
			}
			if (ben.firstName || ben.lastName) {
				return [ben.firstName, ben.lastName].filter(Boolean).join(' ');
			}
			return ben.customerId;
		};

		// Filter and map contacts - strip IDs for new creation
		const contacts = (customer.customerContacts ?? [])
			.filter(contact => !contact.isDeleted)
			.map(contact => ({
				// Omit customerContactId - will be generated on create
				contactTypeId: contact.contactTypeId !== null && contact.contactTypeId !== undefined ? String(contact.contactTypeId) : '',
				contactValue: contact.contactValue ?? '',
				isPrimary: contact.isPrimary,
				notes: contact.notes ?? ''
			}));

		// Filter and map addresses - strip IDs for new creation
		const addresses = (customer.customerAddresses ?? []).map(address => ({
			// Omit customerAddressId and addressId - will be generated on create
			addressTypeId: address.addressTypeId !== null && address.addressTypeId !== undefined ? String(address.addressTypeId) : '',
			streetAddress: address.address?.streetAddress ?? '',
			city: address.address?.city ?? '',
			state: address.address?.state ?? '',
			postalCode: address.address?.postalCode ?? '',
			country: address.address?.country ?? ''
		}));

		// Filter and map bank details - strip IDs for new creation
		const bankDetails = (customer.bankDetails ?? [])
			.filter(detail => !detail.isDeleted)
			.map(detail => ({
				// Omit bankDetailsId - will be generated on create
				bankName: detail.bankName ?? '',
				accountNumber: detail.accountNumber ?? '',
				iban: detail.iban ?? '',
				bankCode: detail.bankCode ?? '',
				accountHolderName: detail.accountHolderName ?? '',
				isPrimary: detail.isPrimary
			}));

		// Filter and map beneficiaries - strip IDs for new creation
		const beneficiaries = (customer.customerBeneficiaries ?? [])
			.filter(beneficiary => !beneficiary.isDeleted)
			.map(beneficiary => ({
				// Omit customerBeneficiaryId - will be generated on create
				beneficiaryCustomerId: beneficiary.beneficiaryId,
				beneficiaryDisplayName: getBeneficiaryDisplayName(beneficiary),
				isActive: beneficiary.isActive,
				notes: beneficiary.notes ?? '',
				relationshipType: beneficiary.relationshipType ?? ''
			}));

		// Map vehicles - strip IDs for new creation
		const vehicles = (customer.vehicles ?? []).map(vehicle => ({
			// Omit vehicleId - will be generated on create
			plateNumber: vehicle.plateNumber ?? '',
			vehicleTypeId: vehicle.vehicleTypeId !== null && vehicle.vehicleTypeId !== undefined ? String(vehicle.vehicleTypeId) : '',
			make: vehicle.make ?? '',
			model: vehicle.model ?? '',
			color: vehicle.color ?? '',
			isActive: vehicle.isActive
		}));

		return {
			// Core identity - add "(Copy)" suffix to name
			isCompany: customer.isCompany,
			firmName: customer.firmName ? `${customer.firmName} (Copy)` : customer.firmName,
			firstName: customer.firstName,
			lastName: customer.lastName,
			email: customer.email,
			fiscalNumber: customer.fiscalNumber,
			customerCategoryId: customer.customerCategoryId,

			// Personal information
			dateOfBirth: customer.dateOfBirth,
			gender: customer.gender,
			nationality: customer.nationality,
			idNumber: customer.idNumber,
			idType: customer.idType,

			// Corporate information
			companyRegistrationNumber: customer.companyRegistrationNumber,
			industry: customer.industry,
			companySize: customer.companySize,

			// Preferences
			preferredLanguage: customer.preferredLanguage,
			notes: customer.notes,
			marketingOptIn: customer.marketingOptIn,
			isActive: customer.isActive,

			// Related entities (without IDs - new ones will be created)
			contacts,
			addresses,
			bankDetails,
			beneficiaries,
			vehicles
		};
	};

	const handleCloneCustomer = async (customerId: string) => {
		try {
			// Fetch full customer data including all related entities
			const { data } = await apolloClient.query<CustomerQueryResponse>({
				query: CUSTOMER_QUERY,
				variables: { id: customerId },
				fetchPolicy: 'network-only'
			});

			const customer = data?.customer;
			if (!customer) {
				toast.error(t('errors.customerNotFound') || 'Customer not found');
				return;
			}

			// Build complete clone data with all fields and related entities
			const cloneData = buildCloneData(customer);

			go({
				to: '/customers/create',
				state: { cloneData }
			});
		} catch (error) {
			console.error('Error fetching customer data for clone:', error);
			toast.error(t('errors.cloneFailed') || 'Failed to clone customer. Please try again.');
		}
	};

	const columns = useMemo<ColDef<CustomerListRecord>[]>(
		() => [
			{
				headerName: t('list.columns.name'),
				field: 'firmName',
				minWidth: 100,
				width: 200,
				flex: 1,
				valueGetter: params => {
					return formatDisplayName(params.data, t);
				},
				cellRenderer: (params: any) => {
					const displayName = formatDisplayName(params.data, t);
					const customerType = params.data?.isCompany
						? 'Business'
						: 'Individual';
					return (
						<div className="flex flex-col py-1 overflow-hidden min-w-0 w-full">
							<div className="font-medium truncate">{displayName}</div>
							<div className="text-xs text-muted-foreground truncate">
								{customerType}
							</div>
						</div>
					);
				},
				sortable: true
			},
			{
				headerName: t('list.columns.email'),
				field: 'email',
				minWidth: 20,
				width: 200,
				valueGetter: params => params.data?.email ?? '',
				sortable: true
			},
			{
				headerName: t('list.columns.fiscalNumber'),
				field: 'fiscalNumber',
				minWidth: 20,
				width: 150,
				valueGetter: params => params.data?.fiscalNumber ?? '',
				sortable: true
			},
			{
				headerName: t('list.columns.category'),
				field: 'customerCategory',
				minWidth: 20,
				width: 150,
				valueGetter: params =>
					params.data?.customerCategory?.description ?? '',
				sortable: true
			},
			{
				headerName: t('list.columns.status'),
				field: 'isActive',
				minWidth: 20,
				width: 120,
				cellRenderer: ActiveInactiveCellRenderer,
				sortable: true
			},
			...(showDeleteColumn ? [{
				headerName: '',
				field: undefined,
				width: 60,
				minWidth: 60,
				maxWidth: 60,
				cellRenderer: DeleteCellRenderer,
				cellRendererParams: {
					onDelete: handleDeleteCustomer,
					disabled: deleteLoading
				},
				sortable: false,
				filter: false,
				resizable: false
			}] : [])
		],
		[t, handleDeleteCustomer, deleteLoading]
	);

	return (
		<>
			{error && (
				<div className="flex items-start p-4">
					<Alert variant="destructive" className="max-w-full">
						<AlertTitle>Unable to load customers</AlertTitle>
						<AlertDescription>
							{error.message ??
								'Failed to retrieve customer data.'}
						</AlertDescription>
					</Alert>
				</div>
			)}
			<ListPageLayout
				gridId="customers-list"
				columns={columns}
				data={customers}
				title={t('list.title')}
				description={t('list.description')}
				searchPlaceholder={t('list.searchPlaceholder')}
				isLoading={loading || deleteLoading}
				selectedItem={selectedCustomer}
				detailsTitle="Customer Details"
				onRowClick={setSelectedCustomer}
				onCreate={goToCreate}
				onEdit={customerId => goToEdit(customerId)}
				onDelete={handleDeleteCustomerById}
				onClone={handleCloneCustomer}
				onOpenInNewWindow={id => openEntityInNewWindow('customers', id)}
				onRefresh={handleRefetch}
				hasNextPage={hasNextPage}
				isLoadingMore={isLoadingMore}
				onLoadMore={handleLoadMore}
				entityName={t('entityName')}
				getRecordId={customer => customer.customerId}
				renderDetailsContent={customer => (
					<CustomerDetails customerId={customer.customerId} />
				)}
				renderDetailsHeader={(customer, actions) => (
					<CustomerDetailsHeader customer={customer} {...actions} />
				)}
			/>
		</>
	);
});

CustomersList.displayName = 'CustomersList';
